<template>
  <div class="contracts">
    <h2 class="my-3">Contracts</h2>
    <ContractListItem
      :contract="contract"
      v-for="(contract, index) in contracts"
      :key="index"
      @onSelect="() => selectContract(contract)"
      :isLoading="isLoading || isFetchingCurrentContract"
    />
    <PDFViewFull :data="fileDialog" />
    <Permissioned section="offers">
      <AddContractAction permission="contract-request" />
    </Permissioned>
  </div>
</template>

<script>
import ContractListItem from "@/views/offers/components/contracts/components/ContractListItem";
import AddContractAction from "@/views/offers/components/contracts/actions/AddContractAction";
import HelloSign from "hellosign-embedded";
import PDFViewFull from "@/components/common/PDFViewFull";
import { getToken } from "@/services/utils";
import { CONTRACT_STATUSES } from "@/views/offers/components/contracts/contants.js";
import {
  FETCH_CURRENT_CONTRACT,
  FETCH_CURRENT_OFFER,
  SIGN_OFFER_CONTRACT,
  REQUEST_SIGN_OFFER_CONTRACT
} from "@/store/modules/offers/actions";
import { OFFERS_NAMESPACE } from "@/store/modules/offers";
import { createNamespacedHelpers } from "vuex";
import { isPermissioned } from "@/utils/permissions";
import { get } from "lodash";
import Permissioned from "@/components/common/Permissioned";

const client = new HelloSign();

const {
  mapActions: mapOfferActions,
  mapState: mapOfferState
} = createNamespacedHelpers(OFFERS_NAMESPACE);

export default {
  components: {
    ContractListItem,
    AddContractAction,
    PDFViewFull,
    Permissioned
  },
  props: {
    contracts: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      fileDialog: { dialog: false }
    };
  },
  computed: {
    ...mapOfferState(["currentContract", "isFetchingCurrentContract"]),
    currentOfferId() {
      return this.$route.params.id;
    },
    shouldOpenContractSigning() {
      return (
        isPermissioned("offers.contract-sign") &&
        this.currentContract.status === CONTRACT_STATUSES.PENDING
      );
    },
    shouldOpenContractRequestSigning() {
      return (
        isPermissioned("offers.contract-request") &&
        this.currentContract.status === CONTRACT_STATUSES.INCOMPLETE
      );
    }
  },
  methods: {
    ...mapOfferActions({
      signOfferContract: SIGN_OFFER_CONTRACT,
      fetchCurrentContract: FETCH_CURRENT_CONTRACT,
      fetchCurrentOffer: FETCH_CURRENT_OFFER,
      requestSignOfferContract: REQUEST_SIGN_OFFER_CONTRACT
    }),
    handleSign() {
      client.once("sign", async () => {
        this.isLoading = true;
        await this.signOfferContract(this.currentContract.id);
        this.isLoading = false;
        this.fetchCurrentOffer(this.currentOfferId);
      });
    },
    handleSignRequest() {
      client.once("send", async () => {
        this.isLoading = true;
        await this.requestSignOfferContract(this.currentContract.id);
        this.isLoading = false;
        this.fetchCurrentOffer(this.currentOfferId);
      });
    },
    openHelloSignWindow(url) {
      client.open(url, {
        clientId: process.env.VUE_APP_HELLO_SIGN_CLIENT_ID,
        skipDomainVerification: true
      });
    },
    handleContractSigning() {
      const signUrl = get(this.currentContract, "links.signUrl");
      this.openHelloSignWindow(signUrl);
      this.handleSign();
    },
    handleContractRequestSigning() {
      const claimUrl = get(this.currentContract, "links.claimUrl");
      this.openHelloSignWindow(claimUrl);
      this.handleSignRequest();
    },
    showFile() {
      const { file } = this.currentContract.links;
      const token = getToken();
      this.fileDialog = {
        src: `${file}?token=${token}`,
        title: this.currentContract.displayName,
        dialog: true
      };
    },
    async selectContract(contract) {
      await this.fetchCurrentContract(contract.id);
      if (this.shouldOpenContractSigning) {
        this.handleContractSigning();
      } else if (this.shouldOpenContractRequestSigning) {
        this.handleContractRequestSigning();
      } else {
        this.showFile();
      }
    }
  }
};
</script>

<style lang="scss">
.contracts {
  margin-bottom: 50px;
  width: 100%;
}
</style>
